import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  TextField,
  Autocomplete,
  Grid,
  Button,
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  SwapVertOutlined,
  SearchOutlined,
  FileDownloadOutlined,
  PictureAsPdfOutlined,
  TableChartOutlined,
} from '@mui/icons-material';
import { reportService, InventoryMovementEntry } from '../../services/reportService';
import { api } from '../../services/api';
import { Item, Partner } from '../../types/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const InventoryMovement: React.FC = () => {
  // State for filters
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [startDate, setStartDate] = useState<string>(() => {
    const today = new Date();
    return today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const today = new Date();
    return today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
  });

  // State for dropdown options
  const [items, setItems] = useState<Item[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [loadingPartners, setLoadingPartners] = useState(true);

  // State for results
  const [movements, setMovements] = useState<InventoryMovementEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [total, setTotal] = useState(0);

  // Export menu state
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);
  const exportMenuOpen = Boolean(exportAnchorEl);

  // Fetch items for dropdown
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await api.get<{ items: Item[] }>('/api/items');
        setItems(response.data.items || []);
      } catch (error) {
        console.error('Error fetching items:', error);
      } finally {
        setLoadingItems(false);
      }
    };
    fetchItems();
  }, []);

  // Fetch partners for dropdown
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await api.get<{ partners: Partner[] }>('/api/partners');
        setPartners(response.data.partners || []);
      } catch (error) {
        console.error('Error fetching partners:', error);
      } finally {
        setLoadingPartners(false);
      }
    };
    fetchPartners();
  }, []);

  // Fetch inventory movements
  const fetchMovements = useCallback(async () => {
    if (!selectedItem) return;

    setLoading(true);
    setHasSearched(true);
    try {
      const response = await reportService.getInventoryMovements({
        item_id: selectedItem.id,
        start_date: startDate,
        end_date: endDate,
        partner_id: selectedPartner?.id,
        page: page + 1,
        limit: rowsPerPage,
      });
      setMovements(response.data || []);
      setTotal(response.meta?.total || 0);
    } catch (error) {
      console.error('Error fetching inventory movements:', error);
      setMovements([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [selectedItem, selectedPartner, startDate, endDate, page, rowsPerPage]);

  // Handle search button click
  const handleSearch = () => {
    setPage(0);
    fetchMovements();
  };

  // Refetch when pagination changes (only if already searched)
  useEffect(() => {
    if (hasSearched && selectedItem) {
      fetchMovements();
    }
  }, [page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMovementTypeChip = (type: string) => {
    const chipProps: { label: string; color: 'success' | 'error' | 'warning' } = {
      label: type,
      color: 'warning',
    };

    switch (type) {
      case 'INWARD':
        chipProps.color = 'success';
        break;
      case 'OUTWARD':
        chipProps.color = 'error';
        break;
      case 'RETURN':
        chipProps.color = 'warning';
        break;
    }

    return <Chip label={chipProps.label} color={chipProps.color} size="small" />;
  };

  // Export functions
  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const fetchAllForExport = async (): Promise<InventoryMovementEntry[]> => {
    if (!selectedItem) return [];
    
    try {
      const response = await reportService.getInventoryMovements({
        item_id: selectedItem.id,
        start_date: startDate,
        end_date: endDate,
        partner_id: selectedPartner?.id,
        export: true,
      });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching data for export:', error);
      return [];
    }
  };

  const formatDateForExport = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const exportToPDF = async () => {
    handleExportClose();
    setExporting(true);
    
    try {
      const data = await fetchAllForExport();
      if (data.length === 0) {
        alert('No data to export');
        return;
      }

      const doc = new jsPDF();
      
      // Title
      doc.setFontSize(11);
      doc.text('Inventory Movement Report', 14, 8);
      
      // Subtitle with filters
      doc.setFontSize(7);
      doc.text(`Item: ${selectedItem?.name || '-'}`, 14, 13);
      doc.text(`Partner: ${selectedPartner?.name || 'All'}`, 14, 17);
      doc.text(`Period: ${startDate} to ${endDate}`, 14, 21);

      // Table
      const tableData = data.map((movement) => [
        formatDateForExport(movement.date),
        `#${movement.order_id}`,
        movement.partner_name,
        movement.quantity.toString(),
        movement.movement_type,
      ]);

      autoTable(doc, {
        startY: 25,
        head: [['Date', 'Order ID', 'Partner Name', 'Quantity', 'Movement Type']],
        body: tableData,
        styles: {
          fontSize: 6,
          cellPadding: 1,
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        headStyles: {
          fontSize: 6,
          fontStyle: 'bold',
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
        },
        tableLineColor: [200, 200, 200],
        tableLineWidth: 0.1,
      });

      doc.save(`inventory-movement-${selectedItem?.name}-${startDate}-to-${endDate}.pdf`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const exportToExcel = async () => {
    handleExportClose();
    setExporting(true);
    
    try {
      const data = await fetchAllForExport();
      if (data.length === 0) {
        alert('No data to export');
        return;
      }

      const worksheetData = [
        ['Inventory Movement Report'],
        [`Item: ${selectedItem?.name || '-'}`],
        [`Partner: ${selectedPartner?.name || 'All'}`],
        [`Period: ${startDate} to ${endDate}`],
        [],
        ['Date', 'Order ID', 'Partner Name', 'Quantity', 'Movement Type'],
        ...data.map((movement) => [
          formatDateForExport(movement.date),
          movement.order_id,
          movement.partner_name,
          movement.quantity,
          movement.movement_type,
        ]),
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Inventory Movement');
      
      // Set column widths
      worksheet['!cols'] = [
        { wch: 20 }, // Date
        { wch: 12 }, // Order ID
        { wch: 25 }, // Partner Name
        { wch: 10 }, // Quantity
        { wch: 15 }, // Movement Type
      ];

      XLSX.writeFile(workbook, `inventory-movement-${selectedItem?.name}-${startDate}-to-${endDate}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const canSearch = selectedItem && startDate && endDate;

  return (
    <Paper sx={{ p: 2, m: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SwapVertOutlined sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold">
            Inventory Movement Report
          </Typography>
        </Box>
        {hasSearched && !loading && movements.length > 0 && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadOutlined />}
              onClick={handleExportClick}
              disabled={exporting}
            >
              Export
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={exportMenuOpen}
              onClose={handleExportClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={exportToPDF}>
                <ListItemIcon>
                  <PictureAsPdfOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export as PDF</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportToExcel}>
                <ListItemIcon>
                  <TableChartOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export as Excel</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      {/* Description */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Track inventory movements for specific items across a date range.
      </Typography>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Autocomplete
            options={items}
            getOptionLabel={(option) => option.name}
            value={selectedItem}
            onChange={(_event, newValue) => setSelectedItem(newValue)}
            loading={loadingItems}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Item *"
                placeholder="Select an item"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingItems ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Autocomplete
            options={partners}
            getOptionLabel={(option) => `${option.name} (${option.type})`}
            value={selectedPartner}
            onChange={(_event, newValue) => setSelectedPartner(newValue)}
            loading={loadingPartners}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Partner"
                placeholder="All partners"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPartners ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Start Date *"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="End Date *"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSearch}
            disabled={!canSearch || loading}
            startIcon={<SearchOutlined />}
            sx={{ height: '40px' }}
          >
            Search
          </Button>
        </Grid>
      </Grid>

      {/* Results */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : hasSearched ? (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Order ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Partner Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Movement Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No inventory movements found for the selected criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  movements.map((movement, index) => (
                    <TableRow key={`${movement.order_id}-${index}`} hover>
                      <TableCell>
                        <Typography variant="body2">{formatDate(movement.date)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{ color: 'primary.main' }}
                        >
                          #{movement.order_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{movement.partner_name}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {movement.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {getMovementTypeChip(movement.movement_type)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {movements.length > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
            />
          )}
        </>
      ) : (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Select an item and date range, then click Search to view movements
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default InventoryMovement;
